import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ModuleLink as Link } from '../../components/ModuleLink';
import { useApiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { safeJson } from '../../utils';
import type { ChangeLog, SurveyLogData } from '../../types';
import Layout from '../../components/Layout';

const QUESTIONS = [
  {
    key: 'awareness',
    label: 'I am aware of what changed and why',
    leftLabel: '1 = Not at all',
    rightLabel: '10 = Fully aware',
  },
  {
    key: 'adoption',
    label: 'I am working in the new way as intended',
    leftLabel: '1 = Not at all',
    rightLabel: '10 = Fully adopted',
  },
  {
    key: 'communicationAdequacy',
    label: 'The communication about this change was adequate',
    leftLabel: '1 = Very poor',
    rightLabel: '10 = Excellent',
  },
  {
    key: 'trainingSupportAdequacy',
    label: 'The training and support provided was adequate',
    leftLabel: '1 = Very poor',
    rightLabel: '10 = Excellent',
  },
] as const;

export default function Survey() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') ?? '';
  const api = useApiClient();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<'form' | 'done' | 'confirmed'>('form');
  const [log, setLog] = useState<ChangeLog | null>(null);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [concerns, setConcerns] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    api
      .publicGet<ChangeLog>(`/logs/${encodeURIComponent(id)}`)
      .then((data: ChangeLog) => {
        setLog(data);
        const d = safeJson<Partial<SurveyLogData>>(data.logData, {});
        if (d.status === 'Responded') setState('done');
      })
      .catch(() => showToast('Failed to load survey.', 'error'))
      .finally(() => setLoading(false));
  }, [id]);

  function validate() {
    const errs: Record<string, string> = {};
    QUESTIONS.forEach((q) => {
      if (!scores[q.key]) errs[q.key] = 'Please select a score.';
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function submit() {
    if (!validate() || !log) return;
    setSubmitting(true);
    try {
      const avg = parseFloat(
        (Object.values(scores).reduce((a, b) => a + b, 0) / 4).toFixed(2),
      );
      const updatedData: SurveyLogData = {
        status: 'Responded',
        awareness: scores.awareness,
        adoption: scores.adoption,
        communicationAdequacy: scores.communicationAdequacy,
        trainingSupportAdequacy: scores.trainingSupportAdequacy,
        unresolvedConcerns: concerns.trim() || undefined,
        averageScore: avg,
      };
      await api.publicPatch(`/logs/${encodeURIComponent(log.id)}`, {
        logData: JSON.stringify(updatedData),
        loggedOn: new Date().toISOString(),
      });
      setState('confirmed');
    } catch {
      showToast('Submission failed.', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Layout maxWidth='form'>
          <nav className='breadcrumb'>
            <Link to='/' className='breadcrumb__link'><i className='fa-solid fa-house' aria-hidden='true' /> Home</Link>
            <span className='breadcrumb__sep'>/</span>
            <span className='breadcrumb__current'>Post-Implementation Survey</span>
          </nav>
          {loading && (
            <div className='loading-state'>
              <div className='spinner spinner--lg' />
              <span>Loading survey…</span>
            </div>
          )}
        {!loading && state === 'done' && (
          <div>
            <div className='state-card'>
              <div style={{ fontSize: '3rem', color: 'var(--accent)' }}>
                <i className='fa-solid fa-circle-check' aria-hidden='true' />
              </div>
              <h1 className='state-card__title'>Survey Already Completed</h1>
              <p className='state-card__body'>
                You have already submitted your feedback. Thank you.
              </p>
            </div>
            <Link to='/' className='btn btn--secondary' style={{ marginTop: 16 }}>
              <i className='fa-solid fa-house' aria-hidden='true' /> Back to Home
            </Link>
          </div>
        )}
        {!loading && state === 'confirmed' && (
          <div>
            <div className='alert alert--success'>
              <span className='alert__icon'>
                <i className='fa-solid fa-circle-check' aria-hidden='true' />
              </span>
              <div className='alert__body'>
                <div className='alert__title'>Thank you!</div>
                <p>Your feedback has been recorded.</p>
              </div>
            </div>
            <Link to='/' className='btn btn--secondary' style={{ marginTop: 16 }}>
              <i className='fa-solid fa-house' aria-hidden='true' /> Back to Home
            </Link>
          </div>
        )}
        {!loading && state === 'form' && log && (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--primary-light)',
                  color: 'var(--primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  flexShrink: 0,
                }}
              >
                <i className='fa-solid fa-chart-line' aria-hidden='true' />
              </div>
              <div>
                <h1 style={{ fontSize: '1.25rem' }}>{log.changeName}</h1>
                <p className='text-muted'>Post-Implementation Feedback</p>
              </div>
            </div>
            <div className='divider' />
            <p style={{ marginBottom: 24 }}>
              Please rate your experience of this change. Your feedback is used
              to assess adoption and inform future change programmes.
            </p>

            {QUESTIONS.map((q) => (
              <div key={q.key} style={{ marginBottom: 28 }}>
                <div
                  style={{
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    marginBottom: 12,
                  }}
                >
                  {q.label}
                </div>
                <div
                  className='score-pill-row'
                  role='group'
                  aria-label={`${q.label} score 1–10`}
                >
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type='button'
                      className={`score-pill${scores[q.key] === n ? ' is-selected' : ''}`}
                      aria-label={`Score ${n}`}
                      onClick={() => {
                        setScores((p) => ({ ...p, [q.key]: n }));
                        setErrors((p) => ({ ...p, [q.key]: '' }));
                      }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className='score-pill-labels'>
                  <span>{q.leftLabel}</span>
                  <span>{q.rightLabel}</span>
                </div>
                {errors[q.key] && (
                  <span className='form-error' style={{ marginTop: 4 }}>
                    <i
                      className='fa-solid fa-circle-exclamation'
                      aria-hidden='true'
                    />{' '}
                    {errors[q.key]}
                  </span>
                )}
              </div>
            ))}

            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                Do you have any unresolved concerns?
                <span
                  className='badge badge--muted'
                  style={{ fontWeight: 400, marginLeft: 6 }}
                >
                  Optional
                </span>
              </div>
              <textarea
                className='form-control'
                rows={3}
                value={concerns}
                onChange={(e) => setConcerns(e.target.value)}
                placeholder='Describe any concerns or issues you still have…'
              />
            </div>

            <button
              className={`btn btn--primary btn--lg w-full${submitting ? ' is-loading' : ''}`}
              onClick={submit}
              disabled={submitting}
            >
              <i className='fa-solid fa-paper-plane' aria-hidden='true' />{' '}
              Submit Feedback
            </button>
          </div>
        )}
    </Layout>
  );
}
